import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Zap, Calendar as CalendarIcon, Sparkles, TrendingUp, Target, Search, FileText, HelpCircle, PlayCircle, Menu } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import api, { apiError, storeTokens, clearTokens } from "./api";
import { T, weekDays, todayLabel, downloadBlob, PLAN_LIMITS, PLAN_LABELS } from "./theme";
import { fontImport, responsiveCSS, LogoMark, TabButton, StatCard, ConfirmDialog, TourModal, CommandPalette, VersionBadge, tabTours } from "./components/shared";
import AuthScreen from "./components/AuthScreen";
import Onboarding from "./components/Onboarding";
import { QuickStartWizard } from "./components/VikiWidgets";
import TodayTab from "./components/TodayTab";
import GrowthTab from "./components/GrowthTab";
import ContentTab from "./components/ContentTab";
import TrendTab from "./components/TrendTab";
import ProfileTab from "./components/ProfileTab";
import { GuidePanel, ReportPanel, SettingsPanel } from "./components/Panels";
import Sidebar from "./components/Sidebar";
import AdminPanel from "./components/AdminPanel";
import "./App.css";

export default function App() {
  const [booting, setBooting] = useState(true);
  const [user, setUser] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [staticData, setStaticData] = useState(null);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [version, setVersion] = useState("");
  const [changelog, setChangelog] = useState([]);

  const [tab, setTab] = useState("oggi");
  const [growthSubTab, setGrowthSubTab] = useState("piano");
  const [profileSubTab, setProfileSubTab] = useState("cliente");
  const [viewAll, setViewAll] = useState(false);
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showQuickStart, setShowQuickStart] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [addProfileError, setAddProfileError] = useState("");
  const [generatingAllCaptions, setGeneratingAllCaptions] = useState(false);

  function celebrate(msg) {
    setCelebration(msg);
    setTimeout(() => setCelebration(null), 2200);
  }

  const loadAll = useCallback(async () => {
    const [profRes, persRes] = await Promise.all([api.get("/profiles"), api.get("/personas")]);
    setProfiles(profRes.data);
    setPersonas(persRes.data);
    return profRes.data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [pb, cp, ct, v] = await Promise.all([
          api.get("/playbook"), api.get("/content-pillars"), api.get("/campaign-templates"), api.get("/version"),
        ]);
        setStaticData({
          playbook: pb.data.playbook, recommendedByGoal: pb.data.recommended_by_goal,
          nicheOptions: pb.data.niche_options, goalOptions: pb.data.goal_options,
          pillars: cp.data.pillars, pillarByGoal: cp.data.pillar_by_goal,
          bestPostingDays: cp.data.best_posting_days, bestPostingTip: cp.data.best_posting_tip,
          benchmarks: cp.data.engagement_benchmarks,
          campaigns: ct.data.templates, recommendedCampaignByGoal: ct.data.recommended_by_goal,
          instagramChecklist: ct.data.instagram_checklist,
        });
        setVersion(v.data.version);
        setChangelog(v.data.changelog);
      } catch (e) { /* riprovato al login */ }
      try {
        const { data } = await api.get("/me");
        setUser(data);
        await loadAll();
      } catch (e) {
        // nessuna sessione locale: prova l'SSO del portale AreaPAQ (cookie su .areapaq.it)
        let ssoOk = false;
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sso/session`, { credentials: "include" });
          if (r.ok) {
            const { access_token } = await r.json();
            api.defaults.headers.common.Authorization = `Bearer ${access_token}`;
            storeTokens({ access_token });
            const { data } = await api.get("/me");
            setUser(data);
            await loadAll();
            ssoOk = true;
          }
        } catch (e2) {}
        if (!ssoOk) setUser(false);
      }
      setBooting(false);
    })();
  }, [loadAll]);

  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setShowPalette((v) => !v);
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    const ssoTok = localStorage.getItem("sso_access_token");
    if (ssoTok) api.defaults.headers.common.Authorization = `Bearer ${ssoTok}`;
  }, []);

  const activeProfile = useMemo(() => {
    if (!profiles.length) return null;
    return profiles.find((p) => p.id === activeProfileId) || profiles[0];
  }, [profiles, activeProfileId]);

  const activePublishedCount = activeProfile ? Object.values(activeProfile.calendar || {}).flat().filter((e) => e.status === "programmato").length : 0;
  const [bestDays, setBestDays] = useState(null);
  useEffect(() => {
    if (!activeProfile) { setBestDays(null); return; }
    api.get(`/profiles/${activeProfile.id}/best-days`).then(({ data }) => setBestDays(data)).catch(() => setBestDays(null));
  }, [activeProfile?.id, activePublishedCount]); // eslint-disable-line react-hooks/exhaustive-deps

  function replaceProfile(updated) {
    setProfiles((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }

  async function handleAuthenticated(u) {
    storeTokens(u);
    setUser(u);
    await loadAll();
  }

  async function handleLogout() {
    try { await api.post("/auth/logout"); } catch (e) {}
    clearTokens();
    delete api.defaults.headers.common.Authorization;
    setUser(false); setProfiles([]); setPersonas([]); setActiveProfileId(null);
    setTab("oggi"); setViewAll(false); setShowSettings(false); setShowGuide(false); setShowReport(false);
  }

  async function finishOnboarding(form) {
    const { data } = await api.post("/profiles", { name: form.nome, niche: form.niche, goal: form.goal });
    setProfiles((prev) => [...prev, data]);
    setActiveProfileId(data.id);
    setUser((u) => ({ ...u, onboarding_done: true }));
    setShowAddProfile(false); setViewAll(false);
    setShowQuickStart(true);
  }

  // ---- derived data ----
  const derived = useMemo(() => {
    if (!activeProfile || !staticData) return null;
    const recommended = staticData.recommendedByGoal[activeProfile.goal] || Object.values(staticData.recommendedByGoal)[0];
    const doneCount = recommended.filter(([cat, idx]) => activeProfile.tasks[`${cat}__${idx}`]).length;
    const hasData = activeProfile.history.length > 0;
    const hasPersona = personas.length > 0;
    const score = Math.round((doneCount / recommended.length) * 60 + (hasData ? 20 : 0) + (hasPersona ? 20 : 0));
    const categoryScore = (cat) => {
      const s = activeProfile.category_stats?.[cat];
      return s ? (s.completed || 0) - (s.skipped || 0) : 0;
    };
    const pillarScore = (pillar) => {
      const s = activeProfile.pillar_stats?.[pillar];
      return s ? (s.used || 0) - (s.skipped || 0) : 0;
    };
    const notDoneTasks = recommended.filter(([cat, idx]) => !activeProfile.tasks[`${cat}__${idx}`]);
    const nextTask = notDoneTasks.length
      ? [...notDoneTasks].sort((a, b) => categoryScore(b[0]) - categoryScore(a[0]))[0]
      : undefined;
    const hasLearningSignal = Object.keys(activeProfile.category_stats || {}).length > 0;
    const defaultPillar = staticData.pillarByGoal[activeProfile.goal] || Object.keys(staticData.pillars)[0];
    const suggestedPillar = [...Object.keys(staticData.pillars)].sort((a, b) => {
      const diff = pillarScore(b) - pillarScore(a);
      if (diff !== 0) return diff;
      return a === defaultPillar ? -1 : b === defaultPillar ? 1 : 0;
    })[0];
    const pillarUses = activeProfile.pillar_stats?.[suggestedPillar]?.used || 0;
    const suggestedIdea = staticData.pillars[suggestedPillar][pillarUses % staticData.pillars[suggestedPillar].length];
    const currentFollowers = activeProfile.history[activeProfile.history.length - 1]?.followers || 0;
    const previousFollowers = activeProfile.history[activeProfile.history.length - 2]?.followers || currentFollowers;
    const growthPct = previousFollowers ? (((currentFollowers - previousFollowers) / previousFollowers) * 100).toFixed(1) : "0.0";
    const isFreshProfile = doneCount === 0 && Object.values(activeProfile.calendar || {}).flat().length === 0 && !activeProfile.instagram_connected;
    return { recommended, doneCount, hasData, hasPersona, score, categoryScore, pillarScore, notDoneTasks, nextTask,
      hasLearningSignal, suggestedPillar, suggestedIdea, currentFollowers, growthPct, isFreshProfile,
      playbook: staticData.playbook };
  }, [activeProfile, personas, staticData]);

  const alerts = useMemo(() => {
    if (!activeProfile || !derived) return [];
    const out = [];
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayDay = todayLabel();
    if (bestDays?.best_days?.includes(todayDay)) {
      const ready = (activeProfile.calendar[todayDay] || []).filter((e) => e.status === "approvato").length;
      if (ready > 0) {
        out.push(`Oggi (${todayDay}) è uno dei tuoi giorni migliori: hai ${ready} contenut${ready > 1 ? "i" : "o"} approvat${ready > 1 ? "i" : "o"} pront${ready > 1 ? "i" : "o"} da pubblicare in "Contenuti".`);
      }
    }
    if ((activeProfile.streak_count || 0) > 0 && activeProfile.streak_last_date !== todayStr) {
      out.push(`La tua serie di ${activeProfile.streak_count} giorni rischia di interrompersi: fai l'azione di oggi in "Oggi".`);
    }
    const bozze = Object.values(activeProfile.calendar || {}).flat().filter((e) => e.status === "bozza").length;
    if (bozze > 0) out.push(`Hai ${bozze} content${bozze > 1 ? "i" : "o"} in bozza da rivedere in "Contenuti".`);
    if (!derived.hasData) out.push('Aggiungi il primo dato in "Andamento" per completare il punteggio profilo.');
    if (!derived.hasPersona) out.push("Definisci il tuo cliente ideale per contenuti più mirati.");
    return out;
  }, [activeProfile, derived, bestDays]);

  // ---- actions ----
  const actions = {
    refreshProfiles: loadAll,
    replaceProfile,
    toggleTask: async (category, idx) => {
      const key = `${category}__${idx}`;
      const willBeDone = !activeProfile.tasks[key];
      const { data } = await api.patch(`/profiles/${activeProfile.id}/tasks/${encodeURIComponent(key)}`, { done: willBeDone });
      replaceProfile(data);
      if (willBeDone) celebrate("Fatto! Continua così 🔥");
    },
    skipCategory: async (category) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/viki-feedback`, { kind: "category", name: category, event: "skipped" });
      replaceProfile(data);
    },
    skipPillar: async (pillar) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/viki-feedback`, { kind: "pillar", name: pillar, event: "skipped" });
      replaceProfile(data);
    },
    usePillar: async (pillar) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/viki-feedback`, { kind: "pillar", name: pillar, event: "used" });
      replaceProfile(data);
    },
    addToCalendar: async (day, text) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/calendar`, { week_day: day, idea_text: text });
      replaceProfile(data);
    },
    removeFromCalendar: async (entryId) => {
      const { data } = await api.delete(`/profiles/${activeProfile.id}/calendar/${entryId}`);
      replaceProfile(data);
    },
    setEntryStatus: async (entryId, status) => {
      const { data } = await api.patch(`/profiles/${activeProfile.id}/calendar/${entryId}/status`, { status });
      replaceProfile(data);
      if (status === "programmato") celebrate("Pubblicato! ✨ (versione dimostrativa)");
    },
    saveCaption: async (entryId, caption) => {
      await api.patch(`/profiles/${activeProfile.id}/calendar/${entryId}/caption`, { caption });
    },
    applyCampaign: async (campaignId) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/campaigns/${campaignId}/apply`);
      replaceProfile(data);
      celebrate("Campagna applicata al calendario ✨");
    },
    autoFillWeek: async () => {
      const pillarName = staticData.pillarByGoal[activeProfile.goal] || Object.keys(staticData.pillars)[0];
      const ideas = staticData.pillars[pillarName];
      const emptyDays = weekDays.filter((d) => !(activeProfile.calendar[d] || []).length);
      if (emptyDays.length === 0) { celebrate("Il calendario di questa settimana è già pieno"); return; }
      let bestPostingDays = staticData.bestPostingDays;
      let personal = false;
      try {
        const { data: bd } = await api.get(`/profiles/${activeProfile.id}/best-days`);
        bestPostingDays = bd.best_days;
        personal = bd.mode === "personale";
      } catch (e) {}
      const preferred = [...bestPostingDays, ...weekDays.filter((d) => !bestPostingDays.includes(d))];
      const targets = preferred.filter((d) => emptyDays.includes(d)).slice(0, Math.min(4, emptyDays.length));
      let latest = activeProfile;
      for (let i = 0; i < targets.length; i++) {
        const { data } = await api.post(`/profiles/${activeProfile.id}/calendar`, { week_day: targets[i], idea_text: ideas[i % ideas.length] });
        latest = data;
      }
      replaceProfile(latest);
      celebrate(personal ? "Settimana compilata dando priorità ai tuoi giorni migliori ✨" : "Settimana compilata da V.I.K.I. ✨");
    },
    generateAllCaptions: async () => {
      const targets = [];
      weekDays.forEach((day) => {
        (activeProfile.calendar[day] || []).forEach((entry) => {
          if (!entry.caption) targets.push(entry);
        });
      });
      if (targets.length === 0) { celebrate("Le didascalie di questa settimana ci sono già tutte ✨"); return; }
      setGeneratingAllCaptions(true);
      for (const t of targets) {
        try {
          const { data } = await api.post(`/profiles/${activeProfile.id}/caption`, { idea_text: t.text });
          await api.patch(`/profiles/${activeProfile.id}/calendar/${t.id}/caption`, { caption: data.caption });
        } catch (e) { /* continua con le altre */ }
      }
      await loadAll();
      setGeneratingAllCaptions(false);
      celebrate("Didascalie generate per tutta la settimana ✨");
    },
    toggleInstagram: async () => {
      const path = activeProfile.instagram_connected ? "disconnect" : "connect";
      const { data } = await api.post(`/profiles/${activeProfile.id}/instagram/${path}`);
      replaceProfile(data);
    },
    syncInstagram: async () => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/instagram/sync`);
      replaceProfile(data);
      celebrate("Dati sincronizzati da Instagram ✨ (simulazione)");
    },
    addDataPoint: async (date, followers, engagement) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/analytics`, { entry_date: date, followers, engagement_rate: engagement });
      replaceProfile(data);
    },
    setCompetitor: async (name) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/competitor`, { name });
      replaceProfile(data);
    },
    removeCompetitor: async () => {
      const { data } = await api.delete(`/profiles/${activeProfile.id}/competitor`);
      replaceProfile(data);
    },
    addCompetitorPoint: async (date, followers) => {
      const { data } = await api.post(`/profiles/${activeProfile.id}/competitor/entries`, { entry_date: date, followers });
      replaceProfile(data);
    },
    createPersona: async (form) => {
      const { data } = await api.post("/personas", form);
      setPersonas((prev) => [...prev, data]);
    },
    removePersona: (id) => {
      setConfirmAction({
        title: "Eliminare questo cliente ideale?",
        body: "La scheda verrà rimossa definitivamente.",
        confirmLabel: "Elimina",
        onConfirm: async () => {
          await api.delete(`/personas/${id}`);
          setPersonas((prev) => prev.filter((p) => p.id !== id));
          setConfirmAction(null);
        },
      });
    },
  };

  function openAddProfile() {
    const limit = PLAN_LIMITS[user.plan];
    if (profiles.length >= limit) {
      setAddProfileError(`Il piano ${PLAN_LABELS[user.plan]} permette al massimo ${limit} profilo${limit > 1 ? "i" : ""}. Passa a un piano superiore in Impostazioni per aggiungerne altri.`);
      return;
    }
    setAddProfileError("");
    setShowAddProfile(true);
  }

  async function duplicateProfile(id) {
    try {
      const { data } = await api.post(`/profiles/${id}/duplicate`);
      setProfiles((prev) => [...prev, data]);
      setActiveProfileId(data.id);
      setViewAll(false);
      setAddProfileError("");
    } catch (e) {
      setAddProfileError(apiError(e));
    }
  }

  function removeProfile(id) {
    const target = profiles.find((p) => p.id === id);
    setConfirmAction({
      title: "Rimuovere questo profilo?",
      body: `"${target?.name}" e tutti i suoi dati (piano, calendario, andamento) verranno eliminati. L'azione non si può annullare.`,
      confirmLabel: "Rimuovi profilo",
      onConfirm: async () => {
        await api.delete(`/profiles/${id}`);
        setProfiles((prev) => {
          const rest = prev.filter((p) => p.id !== id);
          if (activeProfileId === id) setActiveProfileId(rest[0]?.id ?? null);
          return rest;
        });
        setConfirmAction(null);
      },
    });
  }

  async function changePlan(nextPlan) {
    await api.post("/account/plan", { plan: nextPlan });
    setUser((u) => ({ ...u, plan: nextPlan }));
    setAddProfileError("");
  }

  async function exportAccountData() {
    const { data } = await api.get("/export");
    downloadBlob("sohelp-dati.json", JSON.stringify(data, null, 2), "application/json");
  }

  // ---- render ----
  if (booting || !staticData) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: T.textDim, fontFamily: T.font, gap: 12 }}>
        <style>{`${fontImport} @keyframes pulseFade { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } } .pulse-logo { animation: pulseFade 1.4s ease-in-out infinite; }`}</style>
        <div className="pulse-logo"><LogoMark size={26} /></div>
        <div style={{ fontSize: 13 }}>Caricamento…</div>
      </div>
    );
  }

  if (!user) {
    return <AuthScreen onAuthenticated={handleAuthenticated} version={version} changelog={changelog} />;
  }

  if (!profiles.length) {
    if (user.role === "master") {
      return (
        <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.font, color: T.text, padding: "28px 24px" }}>
          <style>{`${fontImport} ${responsiveCSS} * { box-sizing: border-box; }`}</style>
          {showAddProfile && (
            <Onboarding staticData={staticData} overlay onCancel={() => setShowAddProfile(false)} onFinish={finishOnboarding} />
          )}
          <AdminPanel currentUser={user} standalone onCreateOwnProfile={() => setShowAddProfile(true)} onLogout={handleLogout} />
        </div>
      );
    }
    return <Onboarding staticData={staticData} onFinish={finishOnboarding} version={version} changelog={changelog} />;
  }

  const tabDefs = [
    { id: "oggi", label: "Oggi" }, { id: "contenuti", label: "Contenuti" }, { id: "crescita", label: "Crescita" },
    { id: "andamento", label: "Andamento" }, { id: "profilo", label: "Profilo" },
  ];
  function goToTab(id) { setViewAll(false); setShowGuide(false); setShowReport(false); setShowSettings(false); setShowAdmin(false); setTab(id); }
  const paletteCommands = [
    ...tabDefs.map((t) => ({ label: `Vai a ${t.label}`, hint: "scheda", action: () => goToTab(t.id) })),
    ...(user.role === "master" ? [{ label: "Amministrazione", hint: "pannello", action: () => { setViewAll(false); setShowSettings(false); setShowGuide(false); setShowReport(false); setShowAdmin(true); } }] : []),
    { label: "Il mio piano", hint: "in Crescita", action: () => { goToTab("crescita"); setGrowthSubTab("piano"); } },
    { label: "Campagne", hint: "in Crescita", action: () => { goToTab("crescita"); setGrowthSubTab("campagne"); } },
    { label: "Cliente ideale", hint: "in Profilo", action: () => { goToTab("profilo"); setProfileSubTab("cliente"); } },
    { label: "Link in bio", hint: "in Profilo", action: () => { goToTab("profilo"); setProfileSubTab("bio"); } },
    { label: "Tutti i profili", hint: "vista", action: () => setViewAll(true) },
    ...profiles.map((p) => ({ label: `Passa al profilo: ${p.name}`, hint: "profilo", action: () => { setActiveProfileId(p.id); setViewAll(false); } })),
    { label: "Report cliente", hint: "apri", action: () => { setViewAll(false); setShowReport(true); } },
    { label: "Guida", hint: "apri", action: () => { setViewAll(false); setShowGuide(true); } },
    { label: "Impostazioni account", hint: "apri", action: () => { setViewAll(false); setShowSettings(true); } },
    { label: "Configurazione guidata", hint: "avvia", action: () => setShowQuickStart(true) },
    { label: "Esporta i miei dati", hint: "scarica", action: exportAccountData },
    { label: "Esci dall'account", hint: "logout", action: handleLogout },
  ];

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: T.bg, fontFamily: T.font, color: T.text }}>
      <style>{`
        ${fontImport}
        ${responsiveCSS}
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: ${T.line}; border-radius: 4px; }
        button:focus-visible, input:focus-visible, select:focus-visible { outline: 2px solid ${T.amber}; outline-offset: 1px; }
        button { transition: filter 0.15s ease, transform 0.08s ease, border-color 0.15s ease, background-color 0.15s ease; }
        button:hover:not(:disabled) { filter: brightness(1.08); }
        button:active:not(:disabled) { transform: scale(0.97); }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
        .tab-fade { animation: fadeInUp 0.22s ease; }
        @media print {
          .no-print { display: none !important; }
          .print-area { background: #fff !important; border-color: #ddd !important; }
          .print-area, .print-area * { color: #17181c !important; }
        }
      `}</style>

      <VersionBadge version={version} changelog={changelog} />

      {confirmAction && (
        <ConfirmDialog title={confirmAction.title} body={confirmAction.body} confirmLabel={confirmAction.confirmLabel}
          onConfirm={confirmAction.onConfirm} onCancel={() => setConfirmAction(null)} />
      )}

      {showPalette && <CommandPalette commands={paletteCommands} onClose={() => setShowPalette(false)} />}
      {showTour && tabTours[tab] && <TourModal steps={tabTours[tab]} onClose={() => setShowTour(false)} />}
      {showAddProfile && (
        <Onboarding staticData={staticData} overlay onCancel={() => setShowAddProfile(false)} onFinish={finishOnboarding} />
      )}
      {showQuickStart && activeProfile && (
        <QuickStartWizard profile={activeProfile} staticData={staticData}
          onApplyCampaign={actions.applyCampaign}
          onConnectInstagram={async () => { if (!activeProfile.instagram_connected) await actions.toggleInstagram(); }}
          onClose={() => setShowQuickStart(false)} />
      )}

      {celebration && (
        <div data-testid="celebration-toast" style={{ position: "fixed", top: 56, right: 20, background: T.surfaceAlt, border: `1px solid ${T.amber}`, color: T.text, padding: "10px 16px", borderRadius: 8, fontSize: 13, zIndex: 50 }}>
          {celebration}
        </div>
      )}

      {!sidebarOpen && (
        <button data-testid="mobile-menu-btn" className="menu-toggle no-print" onClick={() => setSidebarOpen(true)} style={{
          position: "fixed", top: 14, left: 16, zIndex: 65, background: T.surface, border: `1px solid ${T.line}`,
          color: T.text, borderRadius: 8, padding: "8px 9px", cursor: "pointer", alignItems: "center", justifyContent: "center",
        }}><Menu size={17} /></button>
      )}
      {sidebarOpen && <div className={`sidebar-backdrop ${sidebarOpen ? "open" : ""}`} onClick={() => setSidebarOpen(false)} />}

      <Sidebar_ />

      <div style={{ flex: 1, padding: "52px 28px 22px", overflowY: "auto" }}>
        {viewAll ? (
          <AllProfilesView />
        ) : showAdmin && user.role === "master" ? (
          <AdminPanel currentUser={user} onBack={() => setShowAdmin(false)} />
        ) : showGuide ? (
          <GuidePanel onBack={() => setShowGuide(false)} />
        ) : showReport ? (
          <ReportPanel profile={activeProfile} personas={personas} score={derived.score} onBack={() => setShowReport(false)} />
        ) : showSettings ? (
          <SettingsPanel user={user} profilesCount={profiles.length} onBack={() => setShowSettings(false)} onChangePlan={changePlan} />
        ) : (
          <>
            <div style={{ marginBottom: 18, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div data-testid="active-profile-name" style={{ fontFamily: T.fontHead, fontSize: 22 }}>{activeProfile.name}</div>
                <div style={{ color: T.textDim, fontSize: 13, marginTop: 2 }}>{activeProfile.niche} · Obiettivo: {activeProfile.goal}</div>
              </div>
              <div className="no-print" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button data-testid="header-search-btn" onClick={() => setShowPalette(true)} title="Cerca (Ctrl/Cmd+K)" style={headerBtnStyle}><Search size={14} /> Cerca</button>
                <button data-testid="header-report-btn" onClick={() => setShowReport(true)} title="Report cliente" style={headerBtnStyle}><FileText size={14} /> Report</button>
                <div style={{ position: "relative" }}>
                  <button data-testid="header-help-btn" onClick={() => setShowHelpMenu((v) => !v)} title="Aiuto" style={headerBtnStyle}><HelpCircle size={14} /> Aiuto</button>
                  {showHelpMenu && (
                    <div style={{ position: "absolute", top: 36, right: 0, width: 220, background: T.surface, border: `1px solid ${T.line}`, borderRadius: 12, padding: 6, zIndex: 30, boxShadow: "0 10px 28px rgba(30,30,60,0.14)" }}>
                      {tabTours[tab] && (
                        <button data-testid="help-tour-btn" onClick={() => { setShowTour(true); setShowHelpMenu(false); }} style={helpMenuItemStyle}>
                          <PlayCircle size={15} color={T.amber} /> Tour di questa scheda
                        </button>
                      )}
                      <button data-testid="help-guide-btn" onClick={() => { setShowGuide(true); setShowHelpMenu(false); }} style={helpMenuItemStyle}>
                        <HelpCircle size={15} color={T.amber} /> Guida completa
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="no-print tab-bar-scroll" style={{ display: "flex", borderBottom: `1px solid ${T.line}`, marginBottom: 22 }}>
              <TabButton testId="tab-oggi" active={tab === "oggi"} onClick={() => setTab("oggi")} icon={Zap}>Oggi</TabButton>
              <TabButton testId="tab-contenuti" active={tab === "contenuti"} onClick={() => setTab("contenuti")} icon={CalendarIcon}>Contenuti</TabButton>
              <TabButton testId="tab-crescita" active={tab === "crescita"} onClick={() => setTab("crescita")} icon={Sparkles}>Crescita</TabButton>
              <TabButton testId="tab-andamento" active={tab === "andamento"} onClick={() => setTab("andamento")} icon={TrendingUp}>Andamento</TabButton>
              <TabButton testId="tab-profilo" active={tab === "profilo"} onClick={() => setTab("profilo")} icon={Target}>Profilo</TabButton>
            </div>

            <div key={tab} className="tab-fade">
              {tab === "oggi" && <TodayTab profile={activeProfile} derived={derived} actions={actions} onOpenQuickStart={() => setShowQuickStart(true)} bestDays={bestDays} onGoToContent={() => setTab("contenuti")} />}
              {tab === "contenuti" && <ContentTab profile={activeProfile} staticData={staticData} actions={actions} generatingAllCaptions={generatingAllCaptions} />}
              {tab === "crescita" && <GrowthTab profile={activeProfile} derived={derived} actions={actions} staticData={staticData} subTab={growthSubTab} setSubTab={setGrowthSubTab} />}
              {tab === "andamento" && <TrendTab profile={activeProfile} staticData={staticData} actions={actions} />}
              {tab === "profilo" && <ProfileTab key={activeProfile.id} profile={activeProfile} personas={personas} actions={actions} subTab={profileSubTab} setSubTab={setProfileSubTab} />}
            </div>
          </>
        )}
      </div>
    </div>
  );

  function Sidebar_() {
    return (
      <Sidebar user={user} profiles={profiles} activeProfile={activeProfile} viewAll={viewAll} alerts={alerts}
        showNotifs={showNotifs} setShowNotifs={setShowNotifs} sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen}
        onSelectProfile={(id) => { setActiveProfileId(id); setViewAll(false); setShowSettings(false); setShowGuide(false); setShowReport(false); setShowAdmin(false); }}
        onViewAll={() => { setViewAll(true); setShowSettings(false); setShowGuide(false); setShowReport(false); setShowAdmin(false); }}
        onNewProfile={openAddProfile} onDuplicate={duplicateProfile} onRemove={removeProfile}
        onQuickStart={() => setShowQuickStart(true)} onSettings={() => { setShowSettings(true); setViewAll(false); setShowAdmin(false); }}
        isMaster={user.role === "master"}
        onAdmin={() => { setShowAdmin(true); setViewAll(false); setShowSettings(false); setShowGuide(false); setShowReport(false); }}
        onExport={exportAccountData} onLogout={handleLogout} addProfileError={addProfileError} />
    );
  }

  function AllProfilesView() {
    return (
      <div data-testid="all-profiles-view">
        <div style={{ fontFamily: T.fontHead, fontSize: 22, marginBottom: 18 }}>Tutti i profili</div>
        <div className="grid-report-2" style={{ marginBottom: 20 }}>
          <StatCard label="Follower totali" value={profiles.reduce((s, p) => s + (p.history[p.history.length - 1]?.followers || 0), 0).toLocaleString("it-IT")} sub={`su ${profiles.length} profili`} />
          <StatCard label="Profilo con più follower" value={[...profiles].sort((a, b) => (b.history[b.history.length - 1]?.followers || 0) - (a.history[a.history.length - 1]?.followers || 0))[0]?.name || "-"} />
        </div>
        <div style={{ background: T.surface, border: `1px solid ${T.line}`, borderRadius: 10, padding: 18 }}>
          <div style={{ fontSize: 13, color: T.textDim, marginBottom: 12 }}>Follower per profilo</div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={profiles.map((p) => ({ name: p.name, follower: p.history[p.history.length - 1]?.followers || 0 }))}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
              <XAxis dataKey="name" stroke={T.textDim} fontSize={12} />
              <YAxis stroke={T.textDim} fontSize={12} />
              <Tooltip contentStyle={{ background: T.surface, border: `1px solid ${T.line}`, color: T.text }} />
              <Bar dataKey="follower" fill={T.amber} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
}

const headerBtnStyle = {
  display: "flex", alignItems: "center", gap: 6, background: T.surface, border: `1px solid ${T.line}`,
  color: T.textDim, borderRadius: 7, padding: "7px 12px", fontSize: 12, cursor: "pointer",
};
const helpMenuItemStyle = {
  display: "flex", alignItems: "center", gap: 8, width: "100%", textAlign: "left", background: "transparent",
  border: "none", color: T.text, borderRadius: 8, padding: "8px 10px", fontSize: 13, cursor: "pointer",
};
